// eslint-disable-next-line no-use-before-define
import React from "react";
import classNames from "classnames";

import { useInterval } from "../../hooks/useInterval";
import style from "./style.module.scss";

interface IActions {
	time: number;
	action: () => void;
}

interface ITimeLeft {
	days: number;
	hours: number;
	minutes: number;
	seconds: number;
}

interface IProps {
	countdown: number;
	compact?: boolean;
	scheduledActions?: IActions[];
	color?: string;
	subColor?: string;
	backgroundColor?: string;
	hasDifferentClockColor?: boolean;
	mobileClockColor?: string;
}

export const Countdown = ({
	countdown,
	compact,
	scheduledActions,
	subColor,
	backgroundColor,
	hasDifferentClockColor,
	mobileClockColor,
}: IProps): JSX.Element => {
	const [time, setTime] = React.useState(countdown);
	const [timeLeft, setTimeLeft] = React.useState<ITimeLeft>({
		days: 0,
		hours: 0,
		minutes: 0,
		seconds: 0,
	});

	const runActions = (actions: IActions[]) => {
		for (const a of actions) {
			a.action();
		}
	};

	const calculateTime = (difference: number) => {
		const second = 1;
		const minute = second * 60;
		const hour = minute * 60;
		const day = hour * 24;

		return {
			days: Math.floor(difference / day) ?? 0,
			hours: Math.floor((difference % day) / hour),
			minutes: Math.floor((difference % hour) / minute),
			seconds: Math.floor((difference % minute) / second),
		};
	};

	React.useEffect(() => {
		setTime(countdown);
	}, [countdown]);

	React.useEffect(() => {
		setTimeLeft(calculateTime(time));

		const hasTime = !!scheduledActions && scheduledActions.map((se) => se.time).includes(time);

		if (scheduledActions && hasTime) {
			const actions = scheduledActions.filter((ac) => ac.time === time);
			runActions(actions);
		}
	}, [time]);

	useInterval(() => {
		setTime((x) => (x > 0 ? x - 1 : 0));
	}, 1000);

	return (
		<div className={classNames(style.stopwatch, { [style.compact]: compact })} style={getBackgroundColor()}>
			<span className={style.label}>Acaba em</span>
			<div className={style.interval}>
				<p
					className={style.days}
					style={{
						color: hasDifferentClockColor ? mobileClockColor : subColor,
					}}
				>{`${timeLeft.days.toString().padStart(2, "0")}`}</p>
				<div
					className={style.name}
					style={{
						color: hasDifferentClockColor ? mobileClockColor : subColor,
					}}
				>
					{timeLeft.days > 1 ? "dias" : "dia"}
				</div>
				<p
					className={classNames(style.time, style.center)}
					style={{
						color: hasDifferentClockColor ? mobileClockColor : subColor,
					}}
				>
					{timeLeft.hours.toString().padStart(2, "0")}
				</p>
				<div
					className={style.name}
					style={{
						color: hasDifferentClockColor ? mobileClockColor : subColor,
					}}
				>
					{timeLeft.hours > 1 ? "horas" : "hora"}
				</div>
				<p
					className={style.time}
					style={{
						color: hasDifferentClockColor ? mobileClockColor : subColor,
					}}
				>
					: {timeLeft.minutes.toString().padStart(2, "0")}
				</p>
				<div
					className={style.name}
					style={{
						color: hasDifferentClockColor ? mobileClockColor : subColor,
					}}
				>
					min
				</div>
				<p
					className={style.time}
					style={{
						color: hasDifferentClockColor ? mobileClockColor : subColor,
					}}
				>
					: {timeLeft.seconds.toString().padStart(2, "0")}
				</p>
				<div
					className={style.name}
					style={{
						color: hasDifferentClockColor ? mobileClockColor : subColor,
					}}
				>
					seg
				</div>
			</div>
		</div>
	);

	function getBackgroundColor(): React.CSSProperties | undefined {
		return backgroundColor ? { backgroundColor } : undefined;
	}
};
